import PropTypes from "prop-types";
import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { feedbackLimitMessage } from "utils/feedbackLimit";

import { useSubmitUnregisteredUserFeedbackMutation } from "components/services/feedback/feedbackService";

import paths from "routes/paths";

// The longest name and phone the API takes from a visitor (VISITOR_NAME_MAX_LENGTH
// and VISITOR_PHONE_MAX_LENGTH in backend/app/schemas/user_schema.py); longer is 422.
const NAME_MAX_LENGTH = 100;
const PHONE_MAX_LENGTH = 30;

const TechnicalIssueForm = ({ hasLabel }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(["feedback"]);

  // State
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    telephone: "",
    comments: "",
    file: null, // File state to handle uploads
  });

  const [createFeedback] = useSubmitUnregisteredUserFeedbackMutation();

  // Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    // Frontend validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    }
    if (!formData.comments.trim()) {
      newErrors.comments = "Comments are required";
    }

    // Check if the telephone number is completely filled
    if (formData.telephone.includes("X")) {
      newErrors.telephone = "Please complete the telephone number.";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return; // Stop submission if there are errors
    }

    const feedbackData = new FormData();
    feedbackData.append("first_name", formData.firstName);
    feedbackData.append("last_name", formData.lastName);
    feedbackData.append("email", formData.email);
    feedbackData.append("phone", formData.telephone);
    feedbackData.append("comments", formData.comments);
    if (formData.file) {
      feedbackData.append("uploaded_file", formData.file);
    }

    try {
      await createFeedback(feedbackData).unwrap();
      toast.success(
        `Successfully submitted as ${formData.firstName} ${formData.lastName}`,
        {
          theme: "colored",
        }
      );
      navigate(paths.cardLogin); // Redirect after successful submission
    } catch (error) {
      const limited = feedbackLimitMessage(error, t);
      if (limited) {
        toast.warning(limited, { theme: "colored", toastId: limited });
        return;
      }
      // Handle backend validation errors
      if (error.data?.detail && Array.isArray(error.data.detail)) {
        const backendErrors = {};
        error.data.detail.forEach((err) => {
          const fieldName = err.loc[err.loc.length - 1]; // Get the last element as field name

          // Map backend field names to frontend field names
          const fieldMapping = {
            first_name: "firstName",
            last_name: "lastName",
            phone: "telephone",
            comments: "comments",
            email: "email",
          };

          const frontendFieldName = fieldMapping[fieldName] || fieldName;
          backendErrors[frontendFieldName] = err.msg;
        });
        setErrors(backendErrors);
      } else {
        toast.error(
          `Failed to submit: ${error.data?.detail || "Unknown error"}`,
          {
            theme: "colored",
          }
        );
      }
    }
  };

  // An edited field loses its error, the server's or the form's own: an error
  // left in place keeps Submit disabled with nothing the visitor can do (#122).
  const clearError = (name) => setErrors((prev) => ({ ...prev, [name]: null }));

  const handleFieldChange = (e) => {
    const { name, value } = e.target;

    // Update the form data state
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    clearError(name);
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      file: e.target.files[0], // Only one file
    }));
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>First Name</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? "First Name" : ""}
          value={formData.firstName}
          name="firstName"
          onChange={handleFieldChange}
          type="text"
          maxLength={NAME_MAX_LENGTH}
          isInvalid={!!errors.firstName}
        />
        <Form.Control.Feedback type="invalid">
          {errors.firstName}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Last Name</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? "Last Name" : ""}
          value={formData.lastName}
          name="lastName"
          onChange={handleFieldChange}
          type="text"
          maxLength={NAME_MAX_LENGTH}
          isInvalid={!!errors.lastName}
        />
        <Form.Control.Feedback type="invalid">
          {errors.lastName}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Email address</Form.Label>}
        <Form.Control
          placeholder={!hasLabel ? "Email address" : ""}
          value={formData.email}
          name="email"
          onChange={handleFieldChange}
          type="email"
          isInvalid={!!errors.email}
        />
        <Form.Control.Feedback type="invalid">
          {errors.email}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Telephone</Form.Label>}
        <InputMask
          // "9" is a digit slot to react-input-mask: the country code's 9 is
          // escaped, so the mask has the ten slots of a Nepali number.
          mask={"+\\977 (999) 999 99 99"}
          maskChar="X"
          value={formData.telephone}
          onChange={(e) => {
            setFormData((prev) => ({ ...prev, telephone: e.target.value }));
            clearError("telephone");
          }}
        >
          {() => (
            <Form.Control
              placeholder={!hasLabel ? "Telephone" : ""}
              type="text"
              maxLength={PHONE_MAX_LENGTH}
              isInvalid={!!errors.telephone}
            />
          )}
        </InputMask>
        <Form.Control.Feedback type="invalid">
          {errors.telephone}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Comments</Form.Label>}
        <Form.Control
          as="textarea"
          rows={3}
          name="comments"
          value={formData.comments}
          onChange={handleFieldChange}
          isInvalid={!!errors.comments}
        />
        <Form.Control.Feedback type="invalid">
          {errors.comments}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        {hasLabel && <Form.Label>Upload File (optional)</Form.Label>}
        <Form.Control type="file" onChange={handleFileChange} />
      </Form.Group>

      <Form.Group className="mb-4">
        <Button
          className="w-100"
          type="submit"
          disabled={
            !formData.firstName ||
            !formData.lastName ||
            !formData.email ||
            !formData.comments ||
            Object.values(errors).some((error) => error !== null)
          }
        >
          Submit
        </Button>
      </Form.Group>
    </Form>
  );
};

TechnicalIssueForm.propTypes = {
  hasLabel: PropTypes.bool,
};

export default TechnicalIssueForm;
